import React from "react";
import { motion } from "framer-motion";
import { UserCircle, Users, Apple, ClipboardList, ArrowUpRight } from "lucide-react";
import { useLang, RESERVE_URL } from "../context/LanguageContext";

const ICONS = { UserCircle, Users, Apple, ClipboardList };

export default function Services() {
  const { t } = useLang();

  return (
    <section id="services" data-testid="services-section" className="relative bg-[#050505] py-24 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-7"
          >
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-[#FFCC00] mb-6">
              / 02 — {t.services.overline}
            </div>
            <h2 data-testid="services-title" className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.92]">
              {t.services.title}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="md:col-span-5 text-base md:text-lg text-white/70 font-body leading-relaxed"
          >
            {t.services.desc}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[s.icon] || UserCircle;
            return (
              <motion.a
                key={i}
                href={RESERVE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative bg-black p-8 md:p-12 hover:bg-[#0a0a0a] transition-colors overflow-hidden text-left"
              >
                {/* yellow slash accent on hover */}
                <div className="absolute top-0 left-0 w-0 h-[2px] bg-[#FFCC00] group-hover:w-full transition-all duration-500" />

                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="w-14 h-14 border border-white/15 flex items-center justify-center mb-8 group-hover:border-[#FFCC00] group-hover:bg-[#FFCC00]/5 transition-colors">
                      <Icon className="text-[#FFCC00]" size={24} />
                    </div>
                    <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 mb-3">
                      0{i + 1} / 0{t.services.items.length}
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl text-white mb-4">
                      {s.title}
                      {s.soon && (
                        <span className="ml-3 inline-flex items-center align-middle px-2.5 py-1 bg-[#FFCC00]/10 border border-[#FFCC00]/30 text-[#FFCC00] text-[10px] font-bold tracking-[0.2em] uppercase">
                          Pròximament
                        </span>
                      )}
                    </h3>
                    <p className="text-sm md:text-base text-white/65 max-w-md font-body leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                  <div className="w-12 h-12 border border-white/15 flex items-center justify-center text-white/60 group-hover:border-[#FFCC00] group-hover:text-[#FFCC00] group-hover:rotate-45 transition-all">
                    <ArrowUpRight size={20} />
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
